import FxxlError from '@fxxl/error';

/**
 * Parsen und Generieren von lokalen URLs.
 *
 * Implementiert das Interface der builtin-Klasse URL <https://developer.mozilla.org/en-US/docs/Web/API/URL>,
 * mit folgenden Besonderheiten:
 *
 * - Die statischen Methoden ´createObjectURL()` und `revokeObjectURL()` sind nicht implementiert
 *   (Nutze einfach die Methoden der URL-Klasse).
 * - Die Propertys `pathname` und `href` sind read-only (da die Aufteilung in den "echten" Pfad und den "Parameter-Pfad"
 *   nicht automatisiert erfolgen kann.
 * - zusätzliches Property `realPathname`: Repräsentiert den Pfad ohne ggf. vorhandene im Pfad kodierte Parameter.
 * - `parameterPath`: der Teil des Pfads, der übergebene Parameter kodiert, analog zu `URL.search`.
 * - `pathParams`: Ein Objekt analog zu `URL.searchParams`, das im Pfad kodierte Parameter enthält.
 * - ACHTUNG: Genau wie `search` und `searchParams` der URL-Klasse sind die Propertys `pathname`, `realPathname`,
 *   `paramPathname` und `pathParams` voneinander abhängig: Wird einer dieser Werte geändert, ändern sich potenziell
 *   auch die anderen.
 *   (Implementationsnotiz: Die Source of Truth für diese vier Werte sind `realPathname` und `pathParams`, die anderen
 *   werden beim Lesezugriff aus diesen beiden generiert und beim Schreibzugriff in diese übersetzt.)
 *
 * Implementationsnotiz:
 * * Wir leiten hier nicht von der builtin-Klasse `URL` ab, da uns die Accessor-Funktionalität,
 *   gemixt mit Javascript-(Prototype)-"Vererbung" nicht erlaubt, das Property 'pathname' im
 *   eigentlichen Sinn zu überschreiben (und das ist nötig, da der Pfad in Fxxl-URLs aus zwei
 *   Teilen besteht: Dem "echten" Pfad, und den im Pfad kodierten Parametern.)
 *   Workarounds existieren, aber im vorliegenden Fall müssten diese sich (in milder Weise) auf
 *   Implementationsdetails des Browsers verlassen, was keine gute Idee ist.
 *   Dasselbe gilt, *mutatis muntandis*, für die Verwendung von Javascript's Proxy-Objekten.
 *   Deshalb reimplementieren wir das gesamte URL-Interface, und delegieren alles bis auf das Pfad-Handling
 *   direkt an eine URL-Instanz.
 */

export default class FxxlURL {

    /**
     * Eine Instanz der builtin-URL-Klasse, an die wir alle Aufrufe delegieren, die wir nicht speziell behandeln.
     *
     * @type URL
     * */
    nativeUrl;

    /**
     * Der "echte" Pfad (meistens ein Alias), also die Pfad-Komponente abzüglich der im Pfad
     * codierten Parameter.
     *
     * @type string
     */
    realPathname;

    /**
     * Ein {@link Map}-artiges Objekt, dass die im Pfad enthaltenen Parameter repräsentiert.
     *
     * Analog zu builtin {@link URL.searchParams|searchParams}, mit dem Unterschied, dass
     * `pathParams` nicht `readonly` ist.
     *
     * @type FxxlURLPathParams
     */
    pathParams;

    /**
     * Die ersten beiden Parameter entsprechen denen der builtin URL-Klasse. Der dritte spezifiziert das
     * initiale Segment des Pfads, der als "echter" Pfad betrachtet werden soll (der Rest wird als Parameter-Pfad
     * interpretiert).
     *
     * Wird das dritte Element nicht übergeben, so wird der gesamte Pfad der URL als "echter" Pfad betrachtet.
     *
     * @param url
     * @param base
     * @param realpath
     */
    constructor(url, base, realpath) {
        if (typeof url === 'undefined') {
            url = '/';
        }
        if (typeof base === 'undefined') {
            base = window.location.origin;
        }
        this.nativeUrl = new URL(url, base);
        if (typeof realpath === 'string') {
            if (this.nativeUrl.pathname.startsWith(realpath)) {
                this.realPathname = realpath;
                this.paramPathname = this.nativeUrl.pathname.slice(realpath.length);
            } else {
                new FxxlError('realpath must be initial segment of path.');
            }
        } else {
            this.realPathname = this.nativeUrl.pathname;
            this.paramPathname = '';
        }
    }

    get pathname() {
        const realPath = this.realPathname.endsWith('/') ? this.realPathname : this.realPathName + '/';
        return realPath + this.pathParams.toString();
    }

    get paramPathname(){
        return this.normalizePath(this.pathParams.toString());
    }

    set paramPathname(value) {
        return this.pathParams = new FxxlURLPathParams(value);
    }

    /**
     * Normalisiert einen Pfad.
     *
     * Dabei werden leere Pfad-Komponenten entfernt und sichergestellt, dass der Pfad mit einem Slash beginnt.
     *
     * @param path {string}
     * @returns {string}
     */
    normalizePath(path) {
        return '/' + path.split('/').filter(emptyStringFilter).join('/');
    }

    /**
     * Proxy für den {@link URL.href}-getter.
     *
     * @returns {string}
     */
    get href() {
        this.nativeUrl.pathname = this.pathname;
        return this.nativeUrl.href;
    }

    /**
     * Proxy für {@link URL.toString}.
     *
     * @returns {string}
     */
    toString() {
        this.nativeUrl.pathname = this.pathname;
        return this.nativeUrl.toString();
    }

    /**
     * Proxy für {@link URL.toJSON}.
     *
     * @returns {string}
     */
    toJSON() {
        this.nativeUrl.pathname = this.pathname;
        return this.nativeUrl.toJSON();
    }

    /**
     * Stellt eine Repräsentation der aktuellen URL bereit.
     *
     * @returns {FxxlURL}
     */
    static getCurrent(realpath) {
        return new FxxlURL(window.location.href, undefined, realpath);
    }

    /** DELEGIERTE PROPERTIES **/
    get hash() {
        return this.nativeUrl.hash;
    }
    set hash(value) {
        return this.nativeUrl.hash = value;
    }
    get host() {
        return this.nativeUrl.host;
    }
    set host(value) {
        return this.nativeUrl.host = value;
    }
    get hostname() {
        return this.nativeUrl.hostname;
    }
    set hostname(value) {
        return this.nativeUrl.hostname = value;
    }

    get origin() {
        return this.nativeUrl.origin;
    }

    get password() {
        return this.nativeUrl.password;
    }

    set password(value) {
        return this.nativeUrl.password = value;
    }

    get port() {
        return this.nativeUrl.port;
    }

    set port(value) {
        return this.nativeUrl.port = value;
    }

    get protocol() {
        return this.nativeUrl.protocol;
    }
    set protocol(value) {
        return this.nativeUrl.protocol = value;
    }

    get search() {
        return this.nativeUrl.search;
    }
    set search(value) {
        return this.nativeUrl.search = value;
    }
    get searchParams() {
        return this.nativeUrl.searchParams;
    }
    set searchParams(value) {
        return this.nativeUrl.searchParams = value;
    }
    get username() {
        return this.nativeUrl.username;
    }
    set username(value) {
        return this.nativeUrl.username = value;
    }
}

/**
 * Repräsentiert einen Satz von Key-Value-Sätzen, die als Teil des Pfades einer URL codiert werden.
 *
 * Das Objekt ist eine built-in Map, mit folgenden Besonderheiten:
 * - Werte müssen immer Arrays sein.
 * - Der Konstruktor nimmt neben einem Iterable auch einen String-Parameter, der als Pfad geparst wird.
 *   Jedes Pfadsegment muss die Form "name.wert1_wert2" haben. Initiale und finale Slashes werden ignoriert.
 * - `toString()` liefert einen String in Pfadsyntax nach demselben Muster, ohne initialen/finalen Slash.
 *   Dabei wird die Reihenfolge der Keys und die Reihenfolge der Werte normalisiert (alphabetische Sortierung).
 */
export class FxxlURLPathParams extends Map {
    constructor(options) {
        if (typeof options === 'string') {
            const segments = options.split('/').filter(emptyStringFilter);
            let values= [];
            for (let i= 0; i < segments.length; i++) {
                let currentSegment = segments[i];
                let previousSegment = i > 0 ? segments[i-1] : null;
                let nextSegment = i < segments.length - 1 ? segments[i+1] : null;
                let  result = FxxlURLPathParams.parsePathSegment(currentSegment, previousSegment, nextSegment);
                if (Array.isArray(result)) {
                    values.push(result);
                } else if (result === false) {
                    new FxxlError(`Path segment "${currentSegment}" does not have the required format "key.value(s)"`);
                }
            }
            super(values);
        } else {
            try {
                super(options);
            } catch (e) {
                new FxxlError(e.message);
            }
        }
    }

    /**
     * Genau wie Map.set(), außer dass die Methode sicherstellt, dass der Wert ein Array ist.
     *
     * @param key
     * @param value
     */
    set(key, value) {
        if (!Array.isArray(value)) {
            new FxxlError('Values must be Arrays');
        } else {
            super.set(key, value);
        }
    }

    /**
     * Gibt einen String zurück, der die gesetzten Parameter als (Teil-)Pfad repräsentiert.
     *
     * @returns {string}
     */
    toString() {
        let segments = [];
        for (const key of Array.from(this.keys()).sort()) {
            segments.push(FxxlURLPathParams.encodePathSegment(key, this.get(key)));
        }
        return segments.sort().join('/');
    }

    /**
     * Untersucht ein Pfadsegment, um festzustellen, ob es einen Parameter codiert und parst es ggf. in ein Key-Value-Paar.
     *
     * Ein Pfadsegment codiert einen Parameter, wenn
     * - es genau einen Punkt in nicht-initialer und nicht-finaler Position hat.
     *   -> Teil vor dem Punkt ist der Key, Teil nach dem Punkt ist ein oder mehrere Werte, getrennt durch '_'.
     * - das Pfadsegment ist 'seite' und das folgende Pfadsegment ist eine Ganzzahl.
     * - das Pfadsegment ist eine Ganzzahl, und das vorhergehende Pfadsegment ist 'seite'
     *
     * Der Rückgabewert ist entweder
     * - ein Array `[key, values]`, wobei `values` ein Array ist.
     * - `true`, wenn das Segment zu einem Parameter gehört, aber selbst keinen Wert darstellt (die Ganzzahl nach "Seite")
     * - `false`, wenn das Segment nicht zu einem Parameter gehört.
     */
    static parsePathSegment(segment, previousSegment, nextSegment) {
        if (/^[^.]+\.[^.]+$/.test(segment)) {
            const pair = segment.split('.');
            return [pair[0], pair[1].split('_')];
        } else if (segment === 'seite' && nextSegment &&/^\d+$/.test(nextSegment)) {
            return ['seite', [parseInt(nextSegment)]];
        } else if (previousSegment === 'seite' && /^\d+$/.test(segment)) {
            return true;
        } else {
            return false;
        }
    }

    static encodePathSegment(key, values) {
        if (key === 'seite') {
            return 'seite/' + values[0];
        } else {
            return key + '.' + encodeURIComponent(values.sort().join('_'));
        }
    }
}

window.FxxlUrl = FxxlURL;
function emptyStringFilter(element) {
    return element !== '';
}